import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  IconButton,
  DialogContentText,
  TableFooter,
  Tooltip,
  Alert,
  capitalize,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import {
  VehicleTypeEnum,
  type ImportedUsedVehicleDefinition,
  type UsedVehicleCard,
} from '../../../api/graphql/generated/schema';

import {
  useGetBodiesQuery,
  useGetBrandsQuery,
  useGetColorsQuery,
  useGetCountriesOfOriginQuery,
  useGetFuelTypesQuery,
  useGetTransmissionTypesQuery,
  type GetUsedVehicleCardQueryResult,
} from '../../../api/graphql/generated/hooks';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { IMPORTED_CONSTS } from '../../importedVehicleDefinitions/edit/constants';
import { createMapping, convertVehicleState } from '../../importedVehicleDefinitions/edit/converters';
import { normalize } from '../../../utils/functionHelper';
import { useDispatch } from 'react-redux';
import { setLoadingStatus } from '../../../redux/loading';

type AcceptedValues = Record<string, boolean>;

function mapPropertyKeyToFieldName(key: string): string {
  switch (key) {
    case 'brandId':
      return 'brand';
    case 'imageUrls':
      return 'photos';
    case 'seats':
      return 'seatsCount';
    default:
      return key;
  }
}

type ChangedProperty = {
  oldValue: unknown;
  newValue: unknown;
  property: keyof ImportedUsedVehicleDefinition;
};

function allAccepted(acceptedValues: AcceptedValues, changedProperties: Array<{ property: string }>): boolean {
  return changedProperties.every((prop) => acceptedValues[prop.property] !== undefined);
}

type Props = {
  vehicleCard: UsedVehicleCard;
  importData: NonNullable<NonNullable<GetUsedVehicleCardQueryResult['data']>['getUsedVehicleCard']['importData']>;
  handleUpdateCard: (card: UsedVehicleCard) => void;
};

function renderValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }
  if (value instanceof Date) {
    return value.toISOString().split('T')[0];
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? 'Pravda' : 'Nepravda';
  }
  return String(value);
}

export default function ImportChangedPropertiesDialog({ vehicleCard, importData, handleUpdateCard }: Props) {
  const { t } = useTranslation();
  const { changedProperties } = importData as { changedProperties: Array<ChangedProperty> };
  const { loading: loadingBrands, data: gqlBrands } = useGetBrandsQuery();
  const { loading: loadingFuelTypes, data: gqlFuelTypes } = useGetFuelTypesQuery();
  const { loading: loadingCountries, data: gqlCountries } = useGetCountriesOfOriginQuery();
  const { loading: loadingBodies, data: gqlBodies } = useGetBodiesQuery();
  const { loading: loadingColors, data: gqlColors } = useGetColorsQuery();
  const { loading: loadingTransmissions, data: gqlTransmissions } = useGetTransmissionTypesQuery();
  const [acceptedValues, setAcceptedValues] = useState<AcceptedValues>({});

  const dispatch = useDispatch();

  const anyLoading =
    loadingBrands || loadingFuelTypes || loadingCountries || loadingBodies || loadingColors || loadingTransmissions;

  useEffect(() => {
    dispatch(setLoadingStatus(anyLoading));
  }, [dispatch, anyLoading]);

  const handleAccept = (property: string, accept: boolean) => {
    return () => {
      setAcceptedValues((prev) => {
        if (prev[property] === accept) {
          const { [property]: _, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          [property]: accept,
        };
      });
    };
  };

  const handleBulkAccept = (accept: boolean) => {
    return () => {
      setAcceptedValues(
        changedProperties.reduce((acc, prop) => {
          acc[prop.property] = accept;
          return acc;
        }, {}),
      );
    };
  };

  const handleSave = () => {
    const allPropertiesAccepted = allAccepted(acceptedValues, changedProperties);

    if (!allPropertiesAccepted) {
      return;
    }

    const brandMapping = createMapping(gqlBrands?.getBrands || []);
    const fuelTypeMapping = createMapping(gqlFuelTypes?.getFuelTypes || []);
    const colorMapping = createMapping(gqlColors?.getColors || []);
    const transmissionMapping = createMapping(gqlTransmissions?.getTransmissionTypes || []);
    const bodyTypeMapping = createMapping(
      (gqlBodies?.getBodies || []).filter(
        (body) => body.vehicleType.id === vehicleCard.vehicleDefinition.vehicleType.id,
      ),
    );
    const countryOfOriginMapping = createMapping(gqlCountries?.getCountriesOfOrigin || []);

    const updatedVehicleCard: UsedVehicleCard = JSON.parse(JSON.stringify(vehicleCard));
    changedProperties
      .filter((prop) => acceptedValues[prop.property])
      .forEach((prop) => {
        switch (prop.property) {
          case 'brandId':
            updatedVehicleCard.vehicleDefinition = {
              ...updatedVehicleCard.vehicleDefinition,
              brand: {
                ...(updatedVehicleCard.vehicleDefinition?.brand || {}),
                id: brandMapping[normalize(String(prop.newValue))] || '',
                label: String(prop.newValue),
              },
            };
            break;
          case 'imageUrls':
            updatedVehicleCard.images = (prop.newValue as string[]).map((url, idx) => ({
              id: url,
              order: idx,
              mainImage: idx === 0,
              fileName: url.split('/').pop() || `image-${idx}`,
              publicUrl: url,
            }));
            break;
          case 'stkUntil':
          case 'emissionUntil':
          case 'intoOperation':
            updatedVehicleCard.vehicleDefinition[prop.property] = prop.newValue
              ? new Date(prop.newValue as string)
              : undefined;
            break;
          case 'countryOfOrigin':
            updatedVehicleCard.vehicleDefinition.countryOfOrigin ??= {
              id: countryOfOriginMapping[normalize(String(prop.newValue))] || '',
            };
            updatedVehicleCard.vehicleDefinition.countryOfOrigin.id =
              countryOfOriginMapping[normalize(String(prop.newValue))] || '';
            break;
          case 'vehicleType':
            // @ts-expect-error gql types and enum mapping
            updatedVehicleCard.vehicleDefinition.vehicleType =
              String(prop.newValue)?.toLowerCase() === IMPORTED_CONSTS.VEHICLE_TYPE
                ? VehicleTypeEnum.Personal
                : VehicleTypeEnum.Commercial;
            break;
          case 'color':
            updatedVehicleCard.vehicleDefinition.color = {
              color: {
                id: colorMapping[normalize(String(prop.newValue))] || '',
              },
              id: '',
              name: prop.newValue ? capitalize(String(prop.newValue)) : '',
              colorType: {
                id: vehicleCard.vehicleDefinition.color?.colorType.id || '',
              },
            };
            break;
          case 'fuelType':
            updatedVehicleCard.vehicleDefinition.fuelType = {
              id: fuelTypeMapping[normalize(String(prop.newValue))] || '',
              label: fuelTypeMapping[normalize(String(prop.newValue))] ? String(prop.newValue) : '',
            };
            break;
          case 'body':
            updatedVehicleCard.vehicleDefinition.body.id = bodyTypeMapping[normalize(String(prop.newValue))] || '';
            break;
          case 'transmissionType':
            updatedVehicleCard.vehicleDefinition.transmissionType.id =
              transmissionMapping[normalize(String(prop.newValue))] || '';
            break;
          case 'vehicleState':
            updatedVehicleCard.vehicleDefinition.vehicleState = convertVehicleState(String(prop.newValue));
            break;
          default:
            updatedVehicleCard.vehicleDefinition[prop.property] = prop.newValue || '';
        }
      });

    handleUpdateCard(updatedVehicleCard);
  };

  return (
    <Dialog
      fullWidth
      open
      maxWidth='md'
    >
      <DialogTitle>{t('fieldName.import.changedProperties')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('fieldName.import.changedPropertiesReason')}</DialogContentText>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('fieldName.import.changedProperty')}</TableCell>
                <TableCell>{t('fieldName.import.oldValue')}</TableCell>
                <TableCell>{t('fieldName.import.newValue')}</TableCell>
                <TableCell>{t('fieldName.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {changedProperties.map((changedProp) => (
                <TableRow key={changedProp.property}>
                  <TableCell>{t(`fieldName.${mapPropertyKeyToFieldName(changedProp.property)}`)}</TableCell>
                  <TableCell>{renderValue(changedProp.oldValue)}</TableCell>
                  <TableCell>{renderValue(changedProp.newValue)}</TableCell>
                  <TableCell>
                    <Tooltip title={t('fieldName.import.rejectChange')}>
                      <IconButton
                        onClick={handleAccept(changedProp.property, false)}
                        sx={{
                          opacity:
                            acceptedValues[changedProp.property] !== undefined &&
                            acceptedValues[changedProp.property] === false
                              ? 1
                              : 0.4,
                        }}
                        size='small'
                      >
                        <ClearIcon color='error' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('fieldName.import.acceptChange')}>
                      <IconButton
                        onClick={handleAccept(changedProp.property, true)}
                        sx={{
                          opacity:
                            acceptedValues[changedProp.property] !== undefined &&
                            acceptedValues[changedProp.property] === true
                              ? 1
                              : 0.4,
                        }}
                        size='small'
                      >
                        <CheckIcon color='success' />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={3}>{t('fieldName.import.totalAcceptedChanges')}</TableCell>
                <TableCell>
                  <Tooltip title={t('fieldName.import.rejectChange')}>
                    <span>
                      <IconButton
                        onClick={handleBulkAccept(false)}
                        sx={{
                          opacity: 1,
                        }}
                        size='small'
                      >
                        <ClearIcon color='error' />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('fieldName.import.acceptChange')}>
                    <span>
                      <IconButton
                        onClick={handleBulkAccept(true)}
                        sx={{
                          opacity: 1,
                        }}
                        size='small'
                      >
                        <CheckIcon color='success' />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
        <Alert severity='info'>{t('fieldName.import.saveNotice')}</Alert>
      </DialogContent>
      <DialogActions>
        <Tooltip title={!allAccepted(acceptedValues, changedProperties) ? t('fieldName.import.acceptAllChanges') : ''}>
          <span>
            <Button
              disabled={!allAccepted(acceptedValues, changedProperties)}
              onClick={handleSave}
              variant='contained'
              color='primary'
            >
              {t('actions.apply')}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
